/**
 * Refactored App component using new architecture
 *
 * Demonstrates the improved architecture with:
 * - Context providers for global state
 * - Custom hooks for better state management
 * - Separated concerns and responsibilities
 * - Better performance and maintainability
 *
 * @component
 * @returns {JSX.Element} The rendered application
 */

import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import AppProviders from './context/AppProviders.js';
import { useCursor } from './context/CursorContext.js';
import { useTheme } from './context/ThemeContext.js';
import useLandingSequence from './hooks/useLandingSequence.js';
import useHeroScrollJack from './hooks/useHeroScrollJack.js';
import { Footer } from './components/Footer.js';
import { Intro } from './components/Intro/Intro.js';
import { NavBar } from './components/Nav.js';
import { Projects } from './components/Projects/Projects.js';
import { SkillsMarquee } from './components/Intro/SkillsMarquee.js';
import Cursor from './components/Cursor.js';
import CursorAnnotation from './components/CursorAnnotation.js';
import CursorFxDebug from './components/CursorFxDebug.js';
import { HeaderSequence } from './components/HeaderSequence.js';
import PixelTrail from './components/PixelTrail.js';
import { preloadImages } from './services/AssetService.js';
import FillPhysicsCanvas from './components/FillPhysicsCanvas.js';
import ViewTransitionRouter from './components/ViewTransitionRouter.js';
import Reveal from './components/Reveal.js';
import { watchGrids } from './utils/grid.js';
import { scrollToSection } from './utils/navigateToSection.js';
import PalmScene from './components/Palm/PalmScene.js';
import { ENABLE_SHADER_BACKGROUND } from './featureFlags.js';

// Only referenced when the flag is on, so the Three.js chunk is never fetched
// while the background is disabled.
const MikaShaderEffect = React.lazy(
  () => import('./components/ShaderBackground/index.js'),
);

/**
 * Everything that is not the landing page, split out of the main bundle.
 *
 * The homepage stays eager — it is what someone arriving at the site is waiting
 * for, and deferring it would only move its cost later. Everything else was
 * riding along with it: a project page brings the whole block renderer, the
 * media inspector and the in-page editor; /studio brings a second editor that
 * almost nobody will ever open. All of it was in the first 218KB a visitor
 * downloaded before the loader could paint, which mattered because LCP on this
 * site is render delay rather than network — 3.4s of a 3.48s LCP on throttled
 * mobile was the main thread, not the wire.
 */
const ProjectPage = lazy(() => import('./pages/ProjectPage.js'));
const ProjectPreview = lazy(() => import('./pages/ProjectPreview.js'));
const Studio = lazy(() => import('./pages/Studio.js'));

/**
 * What a route shows while its chunk arrives.
 *
 * A ground of the right colour and nothing else. The chunks are small and local
 * so this is usually a frame or two, and anything with content in it would
 * flash — the page transition already covers the change, and a spinner
 * underneath it would be a second thing happening for no reason.
 */
const RouteGround = ({ tone = 'bg-charcoal' }) => (
  <div className={`min-h-screen w-full ${tone}`} aria-hidden='true' />
);

/**
 * Inner App component that uses context hooks
 *
 * This component uses the context hooks and is wrapped by AppProviders
 */
const AppContent = () => {
  // Use context hooks instead of local state
  const { setCursorType } = useCursor();
  const { getThemeColors } = useTheme();
  const seq = useLandingSequence();
  // Freeze scroll until the loader has handed off AND every name row has
  // landed; after that, the first scroll-down (or the scroll cue) snaps past
  // the hero to the Intro and the drained hero is locked off (no scrolling
  // back up into empty space).
  // The hero collapses to nothing once it has been scrolled past, so the jack
  // needs the section itself, not just a signal.
  const heroRef = useRef(null);
  const snapPastHero = useHeroScrollJack(seq.filled, heroRef, seq.drained);

  // Preload critical assets
  useEffect(() => {
    const criticalAssets = [
      // Add critical image paths here
    ];

    if (criticalAssets.length > 0) {
      preloadImages(criticalAssets);
    }
  }, []);

  // Handle cursor reset on app load
  useEffect(() => {
    setCursorType('');
  }, [setCursorType]);

  // Keep every section's 6px background grid phased to the document origin.
  useEffect(() => watchGrids(), []);

  // Arriving from another route with a section in mind.
  //
  // The target travels as router state rather than as a URL hash: the address
  // bar stays at `/`, so a reload starts the page from the top instead of
  // dropping the reader back into whichever section they last jumped to. The
  // state is cleared once consumed, so a later reload cannot replay it either.
  const location = useLocation();
  const navigate = useNavigate();
  const target = location.state?.scrollTo;
  // A project travelling with the target means "put me back on that one in the
  // index". Only the works pane can do that, and only once it has mounted and
  // measured itself — so this asks, and keeps asking briefly, before falling
  // back to the top of the section.
  const wanted = location.state?.project;
  useEffect(() => {
    if (!target) return undefined;
    let id = 0;
    let tries = 0;
    const done = () =>
      navigate(location.pathname, { replace: true, state: null });
    const attempt = () => {
      if (wanted && scrollToSection(target, wanted)) return done();
      if (wanted && tries < 8) {
        tries += 1;
        id = setTimeout(attempt, 80);
        return undefined;
      }
      scrollToSection(target);
      return done();
    };
    id = setTimeout(attempt, 120);
    return () => clearTimeout(id);
  }, [target, wanted, navigate, location.pathname]);

  // Get theme colors
  const themeColors = getThemeColors();

  return (
    <>
      {/* Shader Background — fixed, z:-1, lazy-loaded to keep Three.js off the
          critical path. Behind ENABLE_SHADER_BACKGROUND; when off, the page
          sits on the flat --acid background from :root. */}
      {ENABLE_SHADER_BACKGROUND && (
        <Suspense fallback={null}>
          <MikaShaderEffect />
        </Suspense>
      )}

      {/* Page content — rides over the fixed footer, so it needs an opaque
          background of its own and a stacking position above it. The bottom
          margin is the footer's measured height: that is the scroll distance
          which uncovers the footer, and it is what makes the works section
          read as a curtain lifting off it.
          overflow-x: clip rather than overflow: hidden. `hidden` makes this
          element a scroll container, which silently defeats `position: sticky`
          on every descendant — the sticky element pins to this box instead of
          the viewport and so never moves relative to its section. `clip` gives
          the same horizontal clipping without establishing that container. */}
      <div
        data-trail-clip
        className="text-ultra relative z-[1] bg-acid [overflow-x:clip]"
        style={{
          position: 'relative',
          marginBottom: 'var(--footer-reveal-h, 100svh)',
        }}
      >
        {/* Both full-page canvases live INSIDE this wrapper. The wrapper is
            opaque — it has to be, to cover the fixed footer during the reveal
            — so a canvas outside it is simply painted over, and the hero came
            up empty.

            Order matters: the physics canvas comes first so the palm paints
            over the falling names rather than under them. Both are clipped to
            the band beside the intro copy, so neither ever crosses the text —
            the names fall past the palm, down and to the right, through the
            same band the shooting stars use. */}
        <FillPhysicsCanvas
          active={seq.filling}
          getSpawnRect={seq.getSpawnRect}
          onHandoff={seq.onHandoff}
          onFilled={seq.onFilled}
          onDrained={seq.onDrained}
        />
        <PalmScene />

        {/* Hidden easter egg text — acid on acid, found by selecting it.
            Taken out of flow: in flow it reserved a line of height above the
            hero, which showed as a strip of bare acid at the very top of the
            page once the hero collapsed, and meant "home" never scrolled to
            something that looked like the top. */}
        <p
          className='pointer-events-none absolute left-0 top-0 m-0'
          style={{ color: themeColors.primary }}
        >
          {`if you're reading this, you found a secret ;p`}
        </p>

        {/* Navigation header */}
        <NavBar setCursor={setCursorType} />

        {/* Landing sequence: blocking pixel-water loader → hero-fold region
            that the FillPhysicsCanvas fills with stacked names. */}
        <HeaderSequence
          pct={seq.pct}
          filling={seq.filling}
          handedOff={seq.handedOff}
          filled={seq.filled}
          onCue={snapPastHero}
          nameRef={seq.nameRef}
          heroRef={heroRef}
          secondaryColor={themeColors.secondary}
        />

        {/* Introduction section.
            Deliberately NOT wrapped in <Reveal>: the section is sticky, and a
            transformed ancestor becomes its containing block — the wrapper is
            exactly the section's height, so sticky would have no travel and
            never engage. */}
        <Intro setCursor={setCursorType} />

        {/* Skills marquee */}
        <Reveal delay={0.1}>
          <SkillsMarquee loop={0} />
        </Reveal>

        {/* Projects showcase */}
        <Projects />

        {/* The trail over everything in this wrapper that is not the intro.
            Last in the wrapper on purpose: it is a z-index:0 layer, so paint
            order against the sections (which are positioned but unindexed)
            is DOM order. The intro keeps a surface of its own, mounted inside
            itself, because there the trail has to go UNDER the copy rather
            than over it — see PixelTrail. */}
        <PixelTrail clipTo='[data-trail-clip]' zone='page' />
      </div>

      {/* Outside the content wrapper on purpose. The footer is fixed, and a
          fixed element inside that z-indexed wrapper would be trapped in its
          stacking context and paint OVER the page rather than behind it. Out
          here it sits below the content, which is what lets the works section
          uncover it. */}
      <Footer cursor={''} setCursor={setCursorType} />
    </>
  );
};

/**
 * Refactored App component
 *
 * Uses context providers instead of prop drilling
 * Implements proper separation of concerns
 * Better performance with optimized re-renders
 */
/**
 * Routes keyed by pathname, so moving between two project pages remounts the
 * page rather than reusing it. The pixel-wipe between pages is not in here:
 * ViewTransitionRouter runs every pathname change under the route curtain.
 */
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/" element={<AppContent />} />
      {/* Suspense sits inside each element rather than around <Routes>, so a
          lazy page suspending shows its own ground rather than blanking the
          whole route tree. */}
      <Route
        path="/projects/:slug"
        element={
          <Suspense fallback={<RouteGround />}>
            <ProjectPage />
          </Suspense>
        }
      />
      <Route
        path="/projects/:slug/preview"
        element={
          <Suspense fallback={<RouteGround tone='bg-ultra' />}>
            <ProjectPreview />
          </Suspense>
        }
      />
      {/* Unlinked on purpose: the content editor, which writes nothing on
          its own and hands over a bundle to be committed. */}
      <Route
        path="/studio"
        element={
          <Suspense fallback={<RouteGround tone='bg-[#131318]' />}>
            <Studio />
          </Suspense>
        }
      />
    </Routes>
  );
};

const AppRefactored = () => {
  return (
    <ViewTransitionRouter>
      <AppProviders>
        {/* One cursor for the whole app, outside <Routes>.
            index.css hides the native cursor under `@media (pointer: fine)`
            for the entire document, so the replacement has to exist on every
            route — mounted per-route it left the project pages with no
            visible cursor at all. Keeping it outside <Routes> also means it
            survives the page transition instead of unmounting mid-navigation. */}
        <Cursor />
        {/* One annotation chip for the whole app, beside the cursor and for
            the same reason: every route can label what is under the pointer,
            and it must survive the page transition rather than unmounting
            mid-navigation. */}
        <CursorAnnotation />
        {/* Draws every gravity field. Only reachable in edit mode — it is a
            tool for arguing with the design rather than part of it, and both of
            its ways in were reachable by accident. */}
        <CursorFxDebug />
        <AnimatedRoutes />
      </AppProviders>
    </ViewTransitionRouter>
  );
};

export default AppRefactored;
